import { Wrench, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import aiLogoUrl from "@/assets/svg/ai-logo.svg?url";
import { MarkdownDisplay } from "./MarkdownDisplay";

export type MessageRole = "user" | "assistant" | "system";

export type ChatMessage = {
  role: MessageRole;
  content: string;
  subtype?: "tool_call" | "tool_result" | "error";
};

export function ThinkingBubble() {
  return (
    <div className="flex items-start gap-1.5 max-w-[90%]">
      <img src={aiLogoUrl} alt="" width={18} height={18} className="rounded-full shrink-0 mt-0.5" />
      <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm bg-muted px-3 py-2.5">
        <span className="sr-only">L'agent réfléchit…</span>
        <span className="h-1.5 w-1.5 rounded-full bg-[#94A3B8] animate-bounce [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#94A3B8] animate-bounce [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#94A3B8] animate-bounce" />
      </div>
    </div>
  );
}

export function MessageBubble({ message }: { message: ChatMessage }) {
  if (message.role === "system") {
    const isToolCall = message.subtype === "tool_call";
    const isToolResult = message.subtype === "tool_result";
    const isError = message.subtype === "error";

    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px]",
          isError && "bg-red-50 text-red-600",
          isToolCall && "bg-blue-50 text-blue-700",
          isToolResult && "bg-green-50 text-green-700",
          !isError && !isToolCall && !isToolResult && "bg-muted text-muted-foreground"
        )}
      >
        {isToolCall && <Wrench className="h-2.5 w-2.5 shrink-0" />}
        {isToolResult && <CheckCircle2 className="h-2.5 w-2.5 shrink-0" />}
        {isError && <AlertCircle className="h-2.5 w-2.5 shrink-0" />}
        <span>
          {isToolCall && `Outil\u00a0: ${message.content}`}
          {isToolResult && `${message.content} terminé`}
          {isError && message.content}
          {!isToolCall && !isToolResult && !isError && message.content}
        </span>
      </div>
    );
  }

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="rounded-2xl rounded-tr-sm bg-[#2563EB] px-3 py-1.5 text-[11px] text-white max-w-[85%]">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1.5 max-w-[90%]">
      <img src={aiLogoUrl} alt="" width={18} height={18} className="rounded-full shrink-0 mt-0.5" />
      <div className="rounded-2xl rounded-tl-sm bg-muted px-3 py-1.5 text-[11px]">
        <MarkdownDisplay text={message.content} />
      </div>
    </div>
  );
}
